import { useLayoutEffect, useRef, useState, type CSSProperties, type FC, type ReactNode } from 'react';
import type { JSONContent } from '@tiptap/react';
import { NOME_TIPO_DOCUMENTO, type TipoDocumento } from '../../services/ModeloDocumentoService';
import { ITENS_EXEMPLO, TOTAL_EXEMPLO, COLUNAS_TABELA, brl } from './exemploDocumento';
import './FolhaDocumento.css';

/** O que o cabeçalho do PDF mostra da empresa. Tudo opcional: sem dado, sai o que o PDF sairia. */
export interface EmpresaDaFolha {
    nome: string;
    cnpj?: string;
    logoUrl?: string | null;
    /** A cor da marca (Gerenciar empresa), a mesma que o gerador usa. Inválida ou nula cai no padrão dele. */
    cor?: string | null;
}

interface Props {
    conteudo: JSONContent;
    tipo: TipoDocumento;
    validadeDias: number | null;
    empresa: EmpresaDaFolha;
    /** Chave → valor de exemplo (o `exemplo` de GET Variaveis). */
    exemplos: Record<string, string>;
    /** O corpo no lugar do conteúdo desenhado: é por aqui que o editor entra na folha. */
    children?: ReactNode;
    /** Folha em edição: cabeçalho, título e rodapé ficam apagados, porque quem os escreve é o PDF. */
    molde?: boolean;
}

/* Os mesmos valores do GeradorPdfDocumentoQuestPdf.cs: a folha é uma prévia
   do PDF, não uma tela do app — por isso não segue o tema escuro. */
const COR_PADRAO = '#1F2937';
const corValida = (c?: string | null) => (c && /^#[0-9A-Fa-f]{6}$/.test(c) ? c : COR_PADRAO);


const dataBr = (d: Date) => d.toLocaleDateString('pt-BR');

/** Alguma variável com este prefixo no conteúdo? O título do PDF usa a organização quando ela existe. */
const usaPrefixo = (no: JSONContent, prefixo: string): boolean =>
    (no.type === 'variavel' && String(no.attrs?.chave ?? '').startsWith(prefixo))
    || (no.content ?? []).some(f => usaPrefixo(f, prefixo));

/* O que está entre colchetes é trecho que só a empresa sabe ("[x] dias") —
   marcado na folha para não passar despercebido. */
const comColchetes = (texto: string, chave: string): ReactNode[] =>
    texto.split(/(\[[^\]]+\])/g).filter(Boolean).map((parte, i) =>
        parte.startsWith('[') && parte.endsWith(']')
            ? <span key={`${chave}-${i}`} className="fdx-completar">{parte}</span>
            : parte);

const alinhamento = (no: JSONContent): CSSProperties | undefined => {
    const a = no.attrs?.textAlign;
    return a && a !== 'left' ? { textAlign: a } : undefined;
};

/**
 * A prévia de um modelo de documento desenhada no front, com dados de exemplo
 * (proposta modelos-de-documento-biblioteca.html §6, opção P1). Percorre o
 * mesmo JSON que o gerador de PDF percorre e imita o desenho dele: A4,
 * cabeçalho com a empresa, título, corpo e rodapé.
 *
 * O que muda a cada oportunidade aparece marcado em azul — é o que ensina o
 * que é variável. Não é o PDF: fonte e quebra de página são aproximadas.
 */
const FolhaDocumento: FC<Props> = ({ conteudo, tipo, validadeDias, empresa, exemplos, children, molde }) => {
    const cor = corValida(empresa.cor);
    const hoje = new Date();
    const validade = validadeDias ? new Date(hoje.getTime() + validadeDias * 86_400_000) : null;
    const nomeTipo = NOME_TIPO_DOCUMENTO[tipo];

    /* O que a empresa e o documento já sabem prevalece sobre o exemplo
       genérico do catálogo ("GB Code", "05/10/2026"). */
    const valores: Record<string, string> = {
        ...exemplos,
        'empresa.nome': empresa.nome || exemplos['empresa.nome'] || '',
        'empresa.cnpj': empresa.cnpj || exemplos['empresa.cnpj'] || '',
        'documento.tipo': nomeTipo,
        'documento.numero': 'PRÉVIA',
        'documento.data_emissao': dataBr(hoje),
        'documento.validade': validade ? dataBr(validade) : '',
        'oportunidade.valor_total': brl(TOTAL_EXEMPLO),
        'oportunidade.quantidade_itens': String(ITENS_EXEMPLO.length),
    };

    const destinatario = usaPrefixo(conteudo, 'organizacao.')
        ? (exemplos['organizacao.nome'] ?? 'Exemplo Ltda')
        : (exemplos['contato.nome'] ?? 'Maria Silva');

    const inline = (nos: JSONContent[] | undefined, chave: string): ReactNode[] =>
        (nos ?? []).map((no, i) => {
            const k = `${chave}.${i}`;
            if (no.type === 'variavel') {
                const v = valores[String(no.attrs?.chave ?? '')] ?? '';
                return <span key={k} className="fdx-var">{v}</span>;
            }
            if (no.type === 'hardBreak') return <br key={k} />;
            if (no.type !== 'text') return <span key={k}>{inline(no.content, k)}</span>;
            let el: ReactNode = comColchetes(no.text ?? '', k);
            for (const m of no.marks ?? []) {
                if (m.type === 'bold') el = <strong>{el}</strong>;
                else if (m.type === 'italic') el = <em>{el}</em>;
                else if (m.type === 'underline') el = <u>{el}</u>;
                else if (m.type === 'strike') el = <s>{el}</s>;
                else if (m.type === 'link') el = <span className="fdx-link">{el}</span>;
            }
            return <span key={k}>{el}</span>;
        });

    const blocos = (nos: JSONContent[] | undefined, chave: string): ReactNode[] =>
        (nos ?? []).map((no, i) => {
            const k = `${chave}.${i}`;
            switch (no.type) {
                case 'paragraph':
                    return <p key={k} className="fdx-p" style={alinhamento(no)}>{inline(no.content, k)}</p>;
                case 'heading': {
                    const nivel = Number(no.attrs?.level ?? 1);
                    return (
                        <p key={k} className={`fdx-h fdx-h${Math.min(nivel, 3)}`} style={{ ...alinhamento(no), color: cor }}>
                            {inline(no.content, k)}
                        </p>
                    );
                }
                case 'bulletList':
                case 'orderedList':
                    return (
                        <div key={k} className="fdx-lista">
                            {(no.content ?? []).map((item, j) => (
                                <div key={`${k}.${j}`} className="fdx-item">
                                    <span className="fdx-marca">{no.type === 'orderedList' ? `${j + 1}.` : '•'}</span>
                                    <div className="fdx-item-c">{blocos(item.content, `${k}.${j}`)}</div>
                                </div>
                            ))}
                        </div>
                    );
                case 'blockquote':
                    return <div key={k} className="fdx-citacao">{blocos(no.content, k)}</div>;
                case 'horizontalRule':
                    return <hr key={k} className="fdx-hr" />;
                case 'tabelaItens':
                    return (
                        <table key={k} className="fdx-tab">
                            <colgroup>{COLUNAS_TABELA.map((w, j) => <col key={j} style={w ? { width: w } : undefined} />)}</colgroup>
                            <thead style={{ color: cor, borderColor: cor }}>
                                <tr><th>Código</th><th>Descrição</th><th className="n">Qtd.</th><th className="n">Unitário</th><th className="n">Total</th></tr>
                            </thead>
                            <tbody>
                                {ITENS_EXEMPLO.map(it => (
                                    <tr key={it.codigo}>
                                        <td>{it.codigo}</td><td>{it.descricao}</td><td className="n">{it.qtd}</td>
                                        <td className="n">{brl(it.unit)}</td><td className="n">{brl(it.total)}</td>
                                    </tr>
                                ))}
                                <tr className="fdx-tot"><td colSpan={4} className="n">Total</td><td className="n">{brl(TOTAL_EXEMPLO)}</td></tr>
                            </tbody>
                        </table>
                    );
                default:
                    return <div key={k}>{blocos(no.content, k)}</div>;
            }
        });

    return (
        <div className={`fdx ${molde ? 'is-molde' : ''}`} aria-label={molde ? undefined : `Prévia: ${nomeTipo}`}
            style={{ '--fdx-cor': cor } as CSSProperties}>
            <div className="fdx-cab fdx-auto">
                <div className="fdx-emp">
                    {empresa.logoUrl
                        ? <img className="fdx-logo" src={empresa.logoUrl} alt="" />
                        : <span className="fdx-logo is-letra" style={{ backgroundColor: cor }}>{(empresa.nome || '?').trim().charAt(0).toUpperCase()}</span>}
                    <div>
                        <div className="fdx-emp-n">{empresa.nome}</div>
                        {empresa.cnpj && <div className="fdx-cinza">CNPJ {empresa.cnpj}</div>}
                    </div>
                </div>
                <div className="fdx-num">
                    <div className="fdx-num-t" style={{ color: cor }}>{nomeTipo} · PRÉVIA</div>
                    <div className="fdx-cinza">Emitido em {dataBr(hoje)}</div>
                    {validade && <div className="fdx-cinza">Válido até {dataBr(validade)}</div>}
                </div>
            </div>
            <div className="fdx-filete fdx-auto" style={{ backgroundColor: cor }} />
            <div className="fdx-corpo">
                <p className="fdx-tit fdx-auto" style={{ color: cor }}>{nomeTipo} nº PRÉVIA - {destinatario}</p>
                {children ?? blocos(conteudo.content, 'b')}
            </div>
            <div className="fdx-pe fdx-auto">
                <span>Emitido por {exemplos['vendedor.nome'] ?? 'João Souza'} em {dataBr(hoje)}</span>
                <span>Página 1 de 1</span>
            </div>
        </div>
    );
};

/**
 * A mesa onde a folha fica no painel. A folha mantém os 595px do PDF (a
 * quebra de linha é a mesma); quando não cabe, a mesa a reduz com `zoom`.
 * Em edição não reduz: texto a 55% (celular) não se edita, então a folha
 * passa a ocupar a largura que houver (`is-fluida`) e a quebra deixa de ser
 * a do PDF — o Ver continua mostrando a exata.
 */
export const MesaDaFolha: FC<{ editavel?: boolean; children: ReactNode }> = ({ editavel, children }) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const [zoom, setZoom] = useState(1);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const medir = () => {
            const s = getComputedStyle(el);
            const livre = el.clientWidth - parseFloat(s.paddingLeft) - parseFloat(s.paddingRight);
            setZoom(Math.min(1, Math.max(0.3, livre / 595)));
        };
        medir();
        const ro = new ResizeObserver(medir);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const fluida = !!editavel && zoom < 1;
    return (
        <div ref={ref} className={`fdx-mesa ${fluida ? 'is-fluida' : ''}`}>
            <div className="fdx-mesa-folha" style={zoom < 1 && !fluida ? { zoom } : undefined}>{children}</div>
        </div>
    );
};

export default FolhaDocumento;
